/**
 * ====================================
 * カートページ用JavaScript
 * ====================================
 *
 * 機能:
 * - 数量変更（Ajax非同期更新）
 * - カートアイテム削除
 * - チェックアウト処理
 * - リアルタイム価格更新
 */

// =====================================
// ユーティリティ関数
// =====================================

/**
 * デバウンス関数 - 連続した呼び出しを制御
 */
if (typeof window.debounce !== "function") {
  window.debounce = function (callback, wait = 300) {
    let timerId;
    return function (...args) {
      clearTimeout(timerId);
      timerId = setTimeout(() => {
        callback.apply(this, args);
      }, wait);
    };
  };
}

/**
 * 設定値
 */
if (typeof window.ON_CHANGE_DEBOUNCE_TIMER !== "number") {
  window.ON_CHANGE_DEBOUNCE_TIMER = 300;
}

/**
 * Fetch設定
 */
if (typeof window.fetchConfig !== "function") {
  window.fetchConfig = function () {
    return {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
      credentials: "same-origin",
    };
  };
}

/**
 * Pub/Subイベント
 */
if (typeof window.PUB_SUB_EVENTS === "undefined") {
  window.PUB_SUB_EVENTS = {
    cartUpdate: "cart-update",
  };
}

/**
 * ルート設定
 */
if (typeof window.routes === "undefined") {
  window.routes = {
    cart_url: "/cart",
    cart_change_url: "/cart/change.js",
    cart_update_url: "/cart/update.js",
  };
} else {
  if (!window.routes.cart_change_url) {
    window.routes.cart_change_url = "/cart/change.js";
  }
  if (!window.routes.cart_url) {
    window.routes.cart_url = "/cart";
  }
  if (!window.routes.cart_update_url) {
    window.routes.cart_update_url = "/cart/update.js";
  }
}

/**
 * Pub/Sub実装
 */
if (
  typeof window.subscribe !== "function" ||
  typeof window.publish !== "function"
) {
  const __themeSubscribers = {};

  window.subscribe = function (eventName, callback) {
    if (!eventName || typeof callback !== "function") {
      return () => { };
    }
    if (!__themeSubscribers[eventName]) {
      __themeSubscribers[eventName] = new Set();
    }
    __themeSubscribers[eventName].add(callback);
    return () => {
      __themeSubscribers[eventName]?.delete(callback);
    };
  };

  window.publish = function (eventName, payload) {
    const listeners = __themeSubscribers[eventName];
    if (!listeners || !listeners.size) {
      return;
    }
    listeners.forEach((callback) => {
      try {
        callback(payload || {});
      } catch (error) {
        console.error("Theme pub/sub listener error", error);
      }
    });
  };
}

// =====================================
// カート削除ボタン
// =====================================

class CartRemoveButton extends HTMLElement {
  constructor() {
    super();
    this.addEventListener("click", (event) => {
      event.preventDefault();
      const cartItems =
        this.closest("cart-items") || this.closest("cart-drawer-items");
      if (cartItems) {
        cartItems.updateQuantity(this.dataset.index, 0);
      }
    });
  }
}

customElements.define("cart-remove-button", CartRemoveButton);

// =====================================
// カートアイテム管理
// =====================================

class CartItems extends HTMLElement {
  constructor() {
    super();

    // 内部状態
    this.currentCartState = null;
    this.cartUpdateUnsubscriber = undefined;
    this.lineItemStatusElement = null;
    this.moneyFormat = null;

    // 初期化
    this.refreshLineItemStatusElement();

    // イベントリスナーの設定（constructor内で一度だけ）
    this.setupEventListeners();
  }

  // =====================================
  // ライフサイクル
  // =====================================

  connectedCallback() {
    // Pub/Subイベントの購読
    this.cartUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.cartUpdate,
      (event = {}) => {
        if (event.source === "cart-items") {
          return;
        }
        if (event.cartData) {
          this.applyCartState(event.cartData);
        } else {
          this.onCartUpdate();
        }
      },
    );

    // フォーム送信イベント（一度だけ登録）
    this.setupFormSubmit();
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  // =====================================
  // イベントリスナーのセットアップ
  // =====================================

  setupEventListeners() {
    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    // 数量入力の変更イベント
    this.addEventListener("change", (event) => {
      if (
        event.target.classList &&
        event.target.classList.contains("quantity__input")
      ) {
        debouncedOnChange(event);
      }
    });

    // フォーカスイン時に前の値を保存
    this.addEventListener("focusin", (event) => {
      if (
        event.target.classList &&
        event.target.classList.contains("quantity__input")
      ) {
        event.target.dataset.prevQuantity = event.target.value;
      }
    });
  }

  /**
   * フォーム送信イベントのセットアップ（重複防止）
   */
  setupFormSubmit() {
    const cartForm = document.getElementById("cart");

    if (!cartForm || cartForm.dataset.cartSubmitInitialized) {
      return;
    }

    cartForm.dataset.cartSubmitInitialized = "true";

    // キャプチャフェーズでフォーム送信を制御
    // index.jsから移動した処理
    cartForm.addEventListener(
      "submit",
      (event) => {
        const submitButton = event.submitter;

        // チェックアウトボタンの場合は送信を許可
        if (submitButton && submitButton.getAttribute("name") === "checkout") {
          // disabledチェック
          if (submitButton.hasAttribute("disabled")) {
            event.preventDefault();
            return;
          }

          // ローディング状態を追加（フォーム送信は妨げない）
          submitButton.setAttribute("aria-busy", "true");
          submitButton.classList.add("loading");

          // Shopifyのチェックアウトフローを妨げない
          return;
        }

        // それ以外のフォーム送信はブロック（数量変更などはAjaxで処理）
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        return false;
      },
      true, // キャプチャフェーズで実行
    );
  }

  // =====================================
  // 数量変更処理
  // =====================================

  onChange(event) {
    this.validateQuantity(event);
  }

  validateQuantity(event) {
    const inputValue = Number(event.target.value);
    const lineNumber = this.resolveLineNumber(event.target);
    const strings = window.quickOrderListStrings || {};

    // 最小値・最大値の取得
    const minAttr =
      event.target.dataset.min !== undefined
        ? event.target.dataset.min
        : event.target.getAttribute("min");
    const minValue =
      minAttr === undefined || minAttr === "" ? 0 : Number(minAttr);
    const maxAttr = event.target.getAttribute("max");
    const maxValue = maxAttr ? Number(maxAttr) : Infinity;
    const stepValue = Number(event.target.step || 1);

    const activeElementName =
      document.activeElement &&
        typeof document.activeElement.getAttribute === "function"
        ? document.activeElement.getAttribute("name")
        : null;
    const triggerName = activeElementName || event.target.getAttribute("name");

    // NaNチェック
    if (Number.isNaN(inputValue)) {
      this.setValidity(event, lineNumber, "");
      return;
    }

    // 最大値チェック
    if (Number.isFinite(maxValue) && inputValue > maxValue) {
      window.alert("在庫上限に達しています。これ以上追加できません。");
      event.target.value = maxValue;
      event.target.dataset.prevQuantity = maxValue;
      event.target.setCustomValidity("");
      event.target.reportValidity();
      this.updateQuantity(
        lineNumber,
        maxValue,
        triggerName,
        event.target.dataset.quantityVariantId,
      );
      return;
    }

    // 数量0の処理（削除）
    if (inputValue <= 0 && minValue === 0) {
      // 確認ダイアログのチェック
      const quantityControl = event.target.closest("[data-quantity-control]");
      const shouldConfirm =
        quantityControl &&
        quantityControl.dataset.quantityConfirmRemove === "true";
      const confirmMessage =
        quantityControl && quantityControl.dataset.quantityConfirmMessage
          ? quantityControl.dataset.quantityConfirmMessage
          : "この商品をカートから削除してもよろしいですか？";

      if (shouldConfirm && !window.confirm(confirmMessage)) {
        // キャンセルされた場合、元の値に戻す
        const prevQuantity = Number(event.target.dataset.prevQuantity || 1);
        event.target.value = prevQuantity;
        return;
      }

      event.target.value = 0;
      event.target.dataset.prevQuantity = 0;
      event.target.setCustomValidity("");
      event.target.reportValidity();
      this.updateQuantity(
        lineNumber,
        0,
        triggerName,
        event.target.dataset.quantityVariantId,
      );
      return;
    }

    // バリデーション
    let message = "";
    if (inputValue < minValue) {
      message = strings.min_error
        ? strings.min_error.replace("[min]", minValue)
        : `数量は${minValue}以上で入力してください。`;
    } else if ((inputValue - minValue) % stepValue !== 0) {
      message = strings.step_error
        ? strings.step_error.replace("[step]", stepValue)
        : `数量は${stepValue}刻みで入力してください。`;
    }

    if (message) {
      this.setValidity(event, lineNumber, message);
      return;
    }

    // 正常な値の場合、更新実行
    event.target.setCustomValidity("");
    event.target.reportValidity();
    this.updateQuantity(
      lineNumber,
      inputValue,
      triggerName,
      event.target.dataset.quantityVariantId,
    );
  }

  // =====================================
  // カート更新（Ajax）
  // =====================================

  /**
   * 数量更新API呼び出し
   */
  updateQuantity(line, quantity, name, variantId) {
    const lineNumber = Number(line);
    if (!Number.isFinite(lineNumber) || lineNumber < 1) {
      console.warn("Invalid cart line index", line);
      return;
    }

    console.log("🛒 updateQuantity called:", {
      line: lineNumber,
      quantity,
      name,
      variantId,
    });

    this.enableLoading(lineNumber);
    const lineKey = this.getLineItemKey(lineNumber);

    console.log("🔑 lineKey:", lineKey);

    const requestUrl = this.getCartChangeEndpoint();
    console.log("📡 Request URL:", requestUrl);

    fetch(requestUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
      credentials: "same-origin",
      body: JSON.stringify(
        lineKey ? { id: lineKey, quantity } : { line: lineNumber, quantity },
      ),
    })
      .then((response) => {
        console.log(
          "📥 Response status:",
          response.status,
          response.statusText,
        );
        if (!response.ok) {
          return response
            .json()
            .catch(() => ({}))
            .then((errorData) => {
              console.error("❌ API Error:", errorData);
              throw errorData;
            });
        }
        return response.json();
      })
      .then((cartState) => {
        console.log("✅ Cart state received:", cartState);
        if (
          !cartState ||
          typeof cartState !== "object" ||
          !Array.isArray(cartState.items)
        ) {
          throw new Error("Invalid cart response");
        }
        this.applyCartState(cartState);
        this.updateLiveRegions(lineNumber, "");

        const focusTarget = document.getElementById(`Quantity-${lineNumber}`);
        if (focusTarget) {
          focusTarget.focus();
        }

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: "cart-items",
          cartData: cartState,
          variantId,
        });
      })
      .catch((error) => {
        console.error("💥 updateQuantity failed:", error);
        const message =
          typeof error?.message === "string"
            ? error.message
            : window.cartStrings?.error || "カートの更新に失敗しました。";
        this.updateLiveRegions(lineNumber, message);

        const errors =
          document.getElementById("cart-errors") ||
          document.getElementById("CartDrawer-CartErrors");
        if (errors) {
          errors.textContent = message;
        }
      })
      .finally(() => {
        this.disableLoading(lineNumber);
      });
  }

  /**
   * カート全体の更新（セクション再読み込み）
   */
  onCartUpdate() {
    fetch(`${routes.cart_url}?section_id=d-cart`)
      .then((response) => response.text())
      .then((responseText) => {
        this.renderSectionsFromHTML(responseText);
      })
      .catch((e) => {
        console.error(e);
      });
  }

  // =====================================
  // カート状態の適用
  // =====================================

  /**
   * カート状態をDOMに反映
   */
  applyCartState(cartState) {
    if (!cartState || typeof cartState !== "object") {
      this.onCartUpdate();
      return;
    }

    this.currentCartState = cartState;

    // 空カートの場合
    if (cartState.item_count === 0) {
      this.renderEmptyCartState();
      return;
    }

    // カート内容を更新
    this.refreshFromCartState(cartState);
    this.enableCheckoutButton();
  }

  /**
   * カート状態からDOMを更新（部分更新）
   */
  refreshFromCartState(cartState) {
    if (!cartState || !Array.isArray(cartState.items)) {
      this.onCartUpdate();
      return;
    }

    const itemsContainer = document.querySelector(".page-cart__items");
    if (!itemsContainer) {
      this.onCartUpdate();
      return;
    }

    const domItems = Array.from(
      itemsContainer.querySelectorAll(".page-cart__items__box"),
    );
    const stateKeys = cartState.items.map((item) => item.key);

    // 削除されたアイテムをアニメーション付きで削除
    domItems.forEach((box) => {
      if (!stateKeys.includes(box.dataset.lineKey)) {
        this.removeItemWithAnimation(box);
      }
    });

    // 既存アイテムの更新
    let needsFullRefresh = false;
    cartState.items.forEach((item, index) => {
      const lineNumber = index + 1;
      const box = itemsContainer.querySelector(
        `.page-cart__items__box[data-line-key="${item.key}"]`,
      );
      if (!box) {
        needsFullRefresh = true;
        return;
      }
      this.updateLineBox(box, item, lineNumber);
    });

    // カート合計金額の更新
    const summaryTotalElement = document.querySelector(
      "[data-cart-summary-total]",
    );
    if (summaryTotalElement && cartState.total_price !== undefined) {
      summaryTotalElement.textContent = this.formatMoney(cartState.total_price);
    }

    if (needsFullRefresh) {
      this.onCartUpdate();
    }
  }

  /**
   * アイテムをアニメーション付きで削除
   */
  removeItemWithAnimation(box) {
    box.style.transition =
      "opacity 300ms ease, transform 300ms ease, max-height 300ms ease";
    box.style.opacity = "0";
    box.style.transform = "translateX(-20px)";
    box.style.maxHeight = "0";
    box.style.overflow = "hidden";
    box.style.marginBottom = "0";

    setTimeout(() => {
      box.remove();
    }, 300);
  }

  /**
   * 行アイテムの情報を更新
   */
  updateLineBox(box, item, lineNumber) {
    box.dataset.lineIndex = lineNumber;
    box.dataset.unitPrice = item.final_price;
    box.id = `CartItem-${lineNumber}`;

    const quantityInput = box.querySelector(".quantity__input");
    if (quantityInput) {
      quantityInput.value = item.quantity;
      quantityInput.setAttribute("value", item.quantity);
      quantityInput.dataset.prevQuantity = item.quantity;
      quantityInput.dataset.index = lineNumber;
      quantityInput.dataset.lineKey = item.key;
      quantityInput.dataset.unitPrice = item.final_price;
    }

    // 小計の更新（セール価格 + ディスカウント対応）
    const subtotalBox = box.querySelector(
      ".page-cart__items__box__subtotalBox",
    );

    if (subtotalBox) {
      const hasDiscount = item.original_line_price > item.final_line_price;

      if (hasDiscount) {
        subtotalBox.innerHTML = `
          <p class="p-price is-sale is-cart">
            <span class="sale_price cart-line__total" data-cart-line-total>
              ${this.formatMoney(item.final_line_price)}
            </span>
            <span>
              <s>${this.formatMoney(item.original_line_price)}</s>
            </span>
          </p>
        `;
      } else {
        subtotalBox.innerHTML = `
          <p class="p-price is-cart">
            <span class="cart-line__total" data-cart-line-total>
              ${this.formatMoney(item.final_line_price)}
            </span>
          </p>
        `;
      }
    } else {
      const lineTotalElement = box.querySelector("[data-cart-line-total]");
      if (lineTotalElement) {
        lineTotalElement.textContent = this.formatMoney(item.final_line_price);
      }
    }
  }

  // =====================================
  // 空カート表示
  // =====================================

  renderEmptyCartState() {
    const cartRoot = document.getElementById("Cart");
    if (!cartRoot) {
      return;
    }

    const itemsWrapper = cartRoot.querySelector(".js-cart-items");
    const summaryWrapper = cartRoot.querySelector(".js-cart-summary");

    const title = this.getCartDatasetValue("emptyTitle", "Your cart is empty");
    const body = this.getCartDatasetValue("emptyBody", "");
    const ctaLabel = this.getCartDatasetValue(
      "emptyCtaLabel",
      "Continue shopping",
    );
    const ctaUrl = this.getCartDatasetValue("emptyCtaUrl", "/");
    const loginEnabled =
      this.getCartDatasetValue("emptyLoginEnabled") === "true";
    const loginTitle = this.getCartDatasetValue("emptyLoginTitle", "");
    const loginText = this.getCartDatasetValue("emptyLoginText", "");
    const loginButton = this.getCartDatasetValue("emptyLoginButton", "Log in");
    const loginUrl = this.getCartDatasetValue(
      "emptyLoginUrl",
      "/account/login",
    );

    if (itemsWrapper) {
      let loginBlock = "";
      if (loginEnabled) {
        loginBlock = `
          <div class="page-cart__empty__box">
            <h2 class="page-cart__empty__title"><span>${loginTitle}</span></h2>
            <p class="p-text"><span>${loginText}</span></p>
            <a href="${loginUrl}" class="p-button is-color__cart is-size__large">
              <span>${loginButton}</span>
            </a>
          </div>`;
      }
      itemsWrapper.innerHTML = `
        <div class="page-cart__empty page-cart__empty--ajax">
          <div class="page-cart__empty__box">
            <h2 class="page-cart__empty__title"><span>${title}</span></h2>
            <p class="p-text"><span>${body}</span></p>
            <a href="${ctaUrl}" class="p-button is-color__cart is-size__large">
              <span>${ctaLabel}</span>
            </a>
          </div>
          ${loginBlock}
        </div>`;
    }

    if (summaryWrapper) {
      const totalElement = summaryWrapper.querySelector(
        "[data-cart-summary-total]",
      );
      if (totalElement) {
        totalElement.textContent = this.formatMoney(0);
      }
      const checkoutButton = summaryWrapper.querySelector("#checkout");
      if (checkoutButton) {
        checkoutButton.setAttribute("disabled", "disabled");
      }
    }
  }

  // =====================================
  // DOM更新（セクションレンダリング）
  // =====================================

  renderSectionsFromHTML(sectionHTML) {
    const sections = this.getSectionsToRender();
    let updated = false;

    sections.forEach((section) => {
      updated = this.replaceSectionElement(section, sectionHTML) || updated;
    });

    if (!updated) {
      updated = this.replaceFullCart(sectionHTML);
    }

    if (updated) {
      this.currentCartState = null;
      this.refreshLineItemStatusElement();

      // 数量コントロールの再初期化
      if (typeof window.initQuantityControls === "function") {
        window.initQuantityControls(this);
      }
    }
  }

  getSectionsToRender() {
    return [
      {
        id: "Cart",
        section: "d-cart",
        selector: ".js-cart-items",
      },
      {
        id: "Cart",
        section: "d-cart",
        selector: ".js-cart-summary",
      },
    ];
  }

  replaceSectionElement(section, html) {
    const container = document.getElementById(section.id);
    if (!container) {
      return false;
    }
    const target = section.selector
      ? container.querySelector(section.selector)
      : container;
    if (!target) {
      return false;
    }
    const newContent = this.getSectionInnerHTML(html, section.selector);
    if (newContent === null) {
      return false;
    }
    target.innerHTML = newContent;
    return true;
  }

  replaceFullCart(html) {
    const container = document
      .getElementById("Cart")
      ?.querySelector(".js-contents");
    if (!container) {
      return false;
    }
    const newContent = this.getSectionInnerHTML(html, ".js-contents");
    if (newContent === null) {
      return false;
    }
    container.innerHTML = newContent;
    return true;
  }

  getSectionInnerHTML(html, selector) {
    const parser = new DOMParser().parseFromString(html, "text/html");
    if (!selector) {
      return parser.body.innerHTML;
    }
    const element = parser.querySelector(selector);
    return element ? element.innerHTML : null;
  }

  // =====================================
  // UI更新
  // =====================================

  /**
   * ローディング状態の有効化
   */
  enableLoading(line) {
    const mainCartItems =
      document.getElementById("main-cart-items") ||
      document.getElementById("CartDrawer-CartItems");
    if (mainCartItems) {
      mainCartItems.classList.add("cart__items--disabled");
    }

    const cartItemElements = this.querySelectorAll(
      `#CartItem-${line} .loading__spinner`,
    );
    const cartDrawerItemElements = this.querySelectorAll(
      `#CartDrawer-Item-${line} .loading__spinner`,
    );

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) =>
      overlay.classList.remove("hidden"),
    );

    if (
      document.activeElement &&
      typeof document.activeElement.blur === "function"
    ) {
      document.activeElement.blur();
    }
    if (this.lineItemStatusElement) {
      this.lineItemStatusElement.setAttribute("aria-hidden", false);
    }
  }

  /**
   * ローディング状態の無効化
   */
  disableLoading(line) {
    const mainCartItems =
      document.getElementById("main-cart-items") ||
      document.getElementById("CartDrawer-CartItems");
    if (mainCartItems) {
      mainCartItems.classList.remove("cart__items--disabled");
    }

    const cartItemElements = this.querySelectorAll(
      `#CartItem-${line} .loading__spinner`,
    );
    const cartDrawerItemElements = this.querySelectorAll(
      `#CartDrawer-Item-${line} .loading__spinner`,
    );

    cartItemElements.forEach((overlay) => overlay.classList.add("hidden"));
    cartDrawerItemElements.forEach((overlay) =>
      overlay.classList.add("hidden"),
    );
  }

  /**
   * チェックアウトボタンの有効化
   */
  enableCheckoutButton() {
    const checkoutButton = document.getElementById("checkout");
    if (checkoutButton) {
      checkoutButton.removeAttribute("disabled");
    }
  }

  // =====================================
  // ユーティリティ
  // =====================================

  refreshLineItemStatusElement() {
    this.lineItemStatusElement =
      document.getElementById("shopping-cart-line-item-status") ||
      document.getElementById("CartDrawer-LineItemStatus");
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    if (input) {
      input.value = input.getAttribute("value");
    }
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) ||
      document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) {
      lineItemError.classList.remove("is-active");
      const errorText = lineItemError.querySelector(".cart-item__error-text");
      if (errorText) {
        errorText.textContent = message;
      }
      if (message) {
        lineItemError.classList.add("is-active");
      }
    }

    if (this.lineItemStatusElement) {
      this.lineItemStatusElement.setAttribute("aria-hidden", true);
    }

    const cartStatus =
      document.getElementById("cart-live-region-text") ||
      document.getElementById("CartDrawer-LiveRegionText");
    if (cartStatus) {
      cartStatus.setAttribute("aria-hidden", false);

      setTimeout(() => {
        cartStatus.setAttribute("aria-hidden", true);
      }, 1000);
    }
  }

  getCartChangeEndpoint() {
    const base = routes?.cart_change_url || "/cart/change";
    return base.endsWith(".js") ? base : `${base}.js`;
  }

  getLineItemKey(lineNumber) {
    const lineItem = document.getElementById(`CartItem-${lineNumber}`);
    if (lineItem && lineItem.dataset.lineKey) {
      return lineItem.dataset.lineKey;
    }
    if (this.currentCartState?.items?.[lineNumber - 1]?.key) {
      return this.currentCartState.items[lineNumber - 1].key;
    }
    const input = document.getElementById(`Quantity-${lineNumber}`);
    if (input && input.dataset.lineKey) {
      return input.dataset.lineKey;
    }
    return null;
  }

  resolveLineNumber(input) {
    if (!input) {
      return 1;
    }
    const lineKey = input.dataset?.lineKey;
    if (lineKey && this.currentCartState?.items?.length) {
      const index = this.currentCartState.items.findIndex(
        (item) => item.key === lineKey,
      );
      if (index !== -1) {
        return index + 1;
      }
    }
    const dataIndex = Number(input.dataset?.index);
    if (Number.isFinite(dataIndex) && dataIndex > 0) {
      return dataIndex;
    }
    const match = input.id && input.id.match(/Quantity-(\d+)/);
    if (match) {
      return Number(match[1]);
    }
    return 1;
  }

  getCartDatasetValue(key, fallback = "") {
    const cartRoot = document.getElementById("Cart");
    if (!cartRoot || !cartRoot.dataset) {
      return fallback;
    }
    const value = cartRoot.dataset[key];
    return value !== undefined ? value : fallback;
  }

  getMoneyFormat() {
    if (this.moneyFormat) {
      return this.moneyFormat;
    }
    const cartRoot = document.getElementById("Cart");
    if (cartRoot && cartRoot.dataset.moneyFormat) {
      this.moneyFormat = cartRoot.dataset.moneyFormat;
      return this.moneyFormat;
    }
    if (window.Shopify && typeof Shopify.money_format === "string") {
      this.moneyFormat = Shopify.money_format;
      return this.moneyFormat;
    }
    this.moneyFormat = "{{amount}}";
    return this.moneyFormat;
  }

  formatMoney(cents) {
    if (window.Shopify && typeof Shopify.formatMoney === "function") {
      return Shopify.formatMoney(cents, this.getMoneyFormat());
    }
    const currency =
      (window.Shopify &&
        window.Shopify.currency &&
        window.Shopify.currency.active) ||
      "JPY";
    const amount = cents / 100;
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
    }).format(amount);
  }
}

// カスタム要素の登録
customElements.define("cart-items", CartItems);

// =====================================
// カートノート
// =====================================

if (!customElements.get("cart-note")) {
  customElements.define(
    "cart-note",
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          "input",
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, {
              ...fetchConfig(),
              ...{ body },
            });
          }, ON_CHANGE_DEBOUNCE_TIMER),
        );
      }
    },
  );
}

// =====================================
// 数量ポップオーバー
// =====================================

if (!customElements.get("quantity-popover")) {
  customElements.define(
    "quantity-popover",
    class QuantityPopover extends HTMLElement {
      constructor() {
        super();
        this.mql = window.matchMedia("(min-width: 990px)");
        this.mqlTablet = window.matchMedia("(min-width: 750px)");
        this.infoButtonDesktop = this.querySelector(
          ".quantity-popover__info-button--icon-only",
        );
        this.infoButtonMobile = this.querySelector(
          ".quantity-popover__info-button--icon-with-label",
        );
        this.popoverInfo = this.querySelector(".quantity-popover__info");
        this.closeButton = this.querySelector(".button-close");
        this.eventMouseEnterHappened = false;

        if (this.closeButton) {
          this.closeButton.addEventListener(
            "click",
            this.closePopover.bind(this),
          );
        }

        if (
          this.popoverInfo &&
          this.infoButtonDesktop &&
          this.mqlTablet.matches
        ) {
          this.popoverInfo.addEventListener(
            "mouseleave",
            this.closePopover.bind(this),
          );
        }

        if (this.infoButtonDesktop) {
          this.infoButtonDesktop.addEventListener(
            "click",
            this.togglePopover.bind(this),
          );
          this.infoButtonDesktop.addEventListener(
            "focusout",
            this.closePopover.bind(this),
          );
        }

        if (this.infoButtonMobile) {
          this.infoButtonMobile.addEventListener(
            "click",
            this.togglePopover.bind(this),
          );
        }

        if (this.infoButtonDesktop && this.mqlTablet.matches) {
          this.infoButtonDesktop.addEventListener(
            "mouseenter",
            this.togglePopover.bind(this),
          );
          this.infoButtonDesktop.addEventListener(
            "mouseleave",
            this.closePopover.bind(this),
          );
        }
      }

      togglePopover(event) {
        event.preventDefault();
        if (event.type === "mouseenter") {
          this.eventMouseEnterHappened = true;
        }

        if (event.type === "click" && this.eventMouseEnterHappened) return;

        const button =
          this.infoButtonDesktop && this.mql.matches
            ? this.infoButtonDesktop
            : this.infoButtonMobile;
        const isExpanded = button.getAttribute("aria-expanded") === "true";

        if ((this.mql.matches && !isExpanded) || event.type === "click") {
          button.setAttribute("aria-expanded", !isExpanded);

          this.popoverInfo.toggleAttribute("hidden");

          button.classList.toggle("quantity-popover__info-button--open");

          this.infoButtonDesktop.classList.add(
            "quantity-popover__info-button--icon-only--animation",
          );
        }

        const isOpen = button.getAttribute("aria-expanded") === "true";

        if (isOpen && event.type !== "mouseenter") {
          button.focus();
          button.addEventListener("keyup", (e) => {
            if (e.key === "Escape") {
              this.closePopover(e);
            }
          });
        }
      }

      closePopover(event) {
        event.preventDefault();
        const isButtonChild = this.infoButtonDesktop.contains(
          event.relatedTarget,
        );
        const isPopoverChild = this.popoverInfo.contains(event.relatedTarget);

        const button =
          this.infoButtonDesktop && this.mql.matches
            ? this.infoButtonDesktop
            : this.infoButtonMobile;

        if (!isButtonChild && !isPopoverChild) {
          button.setAttribute("aria-expanded", "false");
          button.classList.remove("quantity-popover__info-button--open");
          this.popoverInfo.setAttribute("hidden", "");
          this.infoButtonDesktop.classList.remove(
            "quantity-popover__info-button--icon-only--animation",
          );
        }

        this.eventMouseEnterHappened = false;
      }
    },
  );
}
